let db
let openPromise
let currentDbName
let currentVersion

const normaliseVersion = v => {
  const n = Number(v)
  if (!Number.isFinite(n) || n < 1) return 1
  return Math.min(Math.floor(n), Number.MAX_SAFE_INTEGER)
}

function deleteDatabase(dbName) {
  return new Promise(resolve => {
    try {
      const request = indexedDB.deleteDatabase(dbName)
      request.onsuccess = () => resolve(true)
      request.onerror = () => resolve(false)
      request.onblocked = () => resolve(false)
    } catch (_) {
      resolve(false)
    }
  })
}

function bindDbLifecycle(database) {
  database.onversionchange = () => {
    try { database.close() } catch (_) {}
    if (db === database) db = null
    openPromise = null
  }
}

function openDatabase(dbName, versionRaw) {
  return new Promise((resolve, reject) => {
    const desired = normaliseVersion(versionRaw)
    const openWithVersion = target => {
      const request = indexedDB.open(dbName, target)
      request.onupgradeneeded = event => {
        const database = event.target.result
        const tx = event.target.transaction
        let store = database.objectStoreNames.contains('products')
          ? tx.objectStore('products')
          : database.createObjectStore('products', { keyPath: 'id' })
        if (!store.indexNames.contains('variantIds')) {
          store.createIndex('variantIds', 'variantIds', { multiEntry: true, unique: false })
        }
        if (!store.indexNames.contains('id')) {
          store.createIndex('id', 'id', { unique: true })
        }
        if (!database.objectStoreNames.contains('prices')) {
          database.createObjectStore('prices', { keyPath: 'key' })
        }
      }
      request.onsuccess = () => {
        bindDbLifecycle(request.result)
        resolve(request.result)
      }
      request.onerror = () => reject(request.error)
    }
    const probe = indexedDB.open(dbName)
    probe.onupgradeneeded = e => { try { e.target.transaction.abort() } catch (_) {} }
    probe.onsuccess = () => {
      const existing = probe.result.version || 0
      probe.result.close()
      if (existing && existing !== desired) {
        deleteDatabase(dbName).then(() => openWithVersion(desired)).catch(reject)
        return
      }
      const target = existing ? Math.max(existing, desired) : desired
      openWithVersion(target)
    }
    probe.onerror = () => {
      openWithVersion(desired)
    }
  })
}

const initIndexedDBStorage = (dbName, versionRaw) => {
  const desired = normaliseVersion(versionRaw)
  if (currentDbName !== dbName || currentVersion !== desired) {
    if (db) {
      try { db.close() } catch (_) {}
    }
    db = null
    openPromise = null
    currentDbName = dbName
    currentVersion = desired
  } else {
    currentDbName = dbName
    currentVersion = desired
  }
  if (!openPromise) {
    openPromise = openDatabase(dbName, versionRaw).then(d => {
      db = d
      return db
    })
  }
  return openPromise
}

async function ensureOpen() {
  if (db) return db
  if (openPromise) {
    db = await openPromise
    return db
  }
  if (!currentDbName) throw new Error('IndexedDB not initialised')
  openPromise = openDatabase(currentDbName, currentVersion).then(d => {
    db = d
    return db
  })
  db = await openPromise
  return db
}

async function withTransaction(storeNames, mode, _function) {
  let database = await ensureOpen()
  try {
    const transaction = database.transaction(storeNames, mode)
    return await _function(transaction)
  } catch (err) {
    const msg = String(err && err.message || '')
    if (/connection is closing|The database connection is closing|InvalidStateError/i.test(msg)) {
      try { database.close() } catch (_) {}
      db = null
      openPromise = null
      database = await ensureOpen()
      const tx = database.transaction(storeNames, mode)
      return await _function(tx)
    }
    throw err
  }
}

const getPriceByProductId = async rawId => {
  const gid = String(rawId).startsWith('gid://shopify/Product/') ? String(rawId) : `gid://shopify/Product/${rawId}`
  return withTransaction(['products'], 'readonly', transaction => new Promise(resolve => {
    const fallback = () => {
      const global = window.global_theme_object || {}
      const prices = global.product_prices && global.product_prices[rawId] && typeof global.product_prices[rawId] === 'object' ? global.product_prices[rawId] : {}
      resolve(prices)
    }
    try {
      const store = transaction.objectStore('products')
      const request = store.get(gid)
      request.onsuccess = () => {
        const result = request.result
        if (result && result.prices && typeof result.prices === 'object') {
          resolve(result.prices)
        } else {
          fallback()
        }
      }
      request.onerror = () => fallback()
    } catch (_) {
      fallback()
    }
  }))
}

const getAllDBProducts = async () => {
  return withTransaction(['products'], 'readonly', transaction => new Promise((resolve, reject) => {
    const store = transaction.objectStore('products')
    const request = store.getAll()
    request.onsuccess = () => resolve(request.result || [])
    request.onerror = () => reject(request.error)
  }))
}

const addProducts = async products => {
  return withTransaction(['products'], 'readwrite', transaction => new Promise((resolve, reject) => {
    try {
      const store = transaction.objectStore('products')
      for (const product of products) {
        if (!product || !product.id) continue
        const toStore = { ...product, variantIds: Array.isArray(product.variants) ? product.variants.map(v => v.id) : [] }
        const request = store.put(toStore)
        request.onerror = () => reject(request.error)
      }
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error || new Error('Transaction aborted'))
    } catch (e) {
      reject(e)
    }
  }))
}

const replaceProducts = async products => {
  return withTransaction(['products'], 'readwrite', transaction => new Promise((resolve, reject) => {
    try {
      const store = transaction.objectStore('products')
      const clearRequest = store.clear()
      clearRequest.onerror = () => reject(clearRequest.error)
      clearRequest.onsuccess = () => {
        products.filter(product => product && product.id).forEach(product => {
          const toStore = { ...product, variantIds: Array.isArray(product.variants) ? product.variants.map(v => v.id) : [] }
          const request = store.put(toStore)
          request.onerror = () => reject(request.error)
        })
      }
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error || new Error('Transaction aborted'))
    } catch (e) {
      reject(e)
    }
  }))
}

const getProductByVariantIdIndexed = async variantId => {
  return withTransaction(['products'], 'readonly', transaction => new Promise((resolve, reject) => {
    const store = transaction.objectStore('products')
    const index = store.index('variantIds')
    const request = index.get(variantId)
    request.onsuccess = () => resolve(request.result || {})
    request.onerror = () => reject(request.error)
  }))
}

const getProductByIdIndexed = async id => {
  return withTransaction(['products'], 'readonly', transaction => new Promise((resolve, reject) => {
    const store = transaction.objectStore('products')
    const index = store.index('id')
    const request = index.get(id)
    request.onsuccess = () => resolve(request.result || {})
    request.onerror = () => reject(request.error)
  }))
}

const getProductsByIdsIndexed = async ids => {
  if (!Array.isArray(ids) || ids.length === 0) return []
  return withTransaction(['products'], 'readonly', transaction => new Promise((resolve, reject) => {
    const store = transaction.objectStore('products')
    const index = store.index('id')
    const out = new Array(ids.length)
    let remaining = ids.length
    ids.forEach((id, item) => {
      const request = index.get(id)
      request.onsuccess = () => {
        out[item] = request.result || null
        remaining -= 1
        if (remaining === 0) resolve(out.filter(Boolean))
      }
      request.onerror = () => reject(request.error)
    })
    transaction.onerror = () => reject(transaction.error)
    transaction.onabort = () => reject(transaction.error || new Error('Transaction aborted'))
  }))
}

export const setPrices = async (key, value) => {
  return withTransaction(['prices'], 'readwrite', transaction => new Promise((resolve, reject) => {
    try {
      const store = transaction.objectStore('prices')
      const entries = Object.entries(value)
      for (const [gid, price] of entries) {
        if (!price) continue
        store.put({ key: gid, value: price })
      }
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error || new Error('Transaction aborted'))
    } catch (e) { reject(e) }
  }))
}

export const getPrices = async key => {
  return withTransaction(['prices'], 'readonly', transaction => new Promise((resolve, reject) => {
    try {
      const store = transaction.objectStore('prices')
      const request = store.get(key)
      request.onsuccess = () => resolve((request.result && request.result.value) || null)
      request.onerror = () => reject(request.error)
    } catch (e) { reject(e) }
  }))
}

export { initIndexedDBStorage, getProductsByIdsIndexed, getAllDBProducts, addProducts, replaceProducts, getPriceByProductId, getProductByVariantIdIndexed, getProductByIdIndexed }
